import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, ChevronDown, AlertTriangle } from "lucide-react";

const LOGO_URL =
  "https://files.manuscdn.com/user_upload_by_module/session_file/310519663448310112/KkdVDzUbtvERWOCX.webp";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services" },
  { label: "Book Online", href: "/book" },
  { label: "Get a Quote", href: "/quote" },
  { label: "Commercial", href: "/commercial" },
  { label: "Gallery", href: "/gallery" },
  { label: "Blog", href: "/blog" },
  { label: "Areas", href: "/locations" },
  { label: "Near Me", href: "/pest-control-near-me" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
  { label: "🇷🇴 Română", href: "/romana" },
];

const TOOLS_LINKS = [
  { label: "❓ FAQ — 250+ Answers", href: "/faq" },
  { label: "📍 Pest Control Near Me", href: "/pest-control-near-me" },
  { label: "🎰 Spin & Win 10% Off", href: "/spin" },
  { label: "💰 Cost Calculator", href: "/calculator" },
  { label: "📅 Pest Calendar", href: "/pest-calendar" },
  { label: "🔍 Identify My Pest", href: "/identify" },
  { label: "💬 Free Pest Advice", href: "/free-advice" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = location === "/";

  return (
    <>
      {/* Emergency top bar */}
      <div className="bg-[oklch(0.72_0.18_65)] text-black text-sm py-2 px-4 text-center font-semibold">
        <span className="inline-flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          24/7 Emergency Pest Control — Same Day Service Available
          <a
            href="tel:07586072087" onClick={() => { if (typeof gtag_report_conversion !== "undefined") gtag_report_conversion("tel:07586072087"); }}
            className="underline hover:no-underline ml-2 font-bold"
          >
            Call Now: 07586 072087
          </a>
        </span>
      </div>

      {/* Main navbar */}
      <nav
        className="sticky top-0 z-50 bg-[oklch(0.22_0.06_255)] shadow-lg"
      >
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3 shrink-0">
              <img
                src={LOGO_URL}
                alt="Pest Control RAF SERV LTD"
                className="h-10 lg:h-12 w-auto object-contain"
                width="120"
                height="48"
                fetchPriority="high"
              />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location === link.href
                      ? "bg-white/20 text-white"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {/* Tools dropdown */}
              <div className="relative group">
                <button className="flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 transition-colors">
                  Tools <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  {TOOLS_LINKS.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-[oklch(0.22_0.06_255)] transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:07586072087" onClick={() => { if (typeof gtag_report_conversion !== "undefined") gtag_report_conversion("tel:07586072087"); }}
                className="flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium transition-colors"
              >
                <Phone className="w-4 h-4" />
                07586 072087
              </a>
              <Button
                asChild
                className="bg-[oklch(0.72_0.18_65)] hover:bg-[oklch(0.65_0.18_65)] text-black font-bold px-5 py-2 rounded-lg shadow-md transition-all hover:shadow-lg"
              >
                <Link href="/book">Book Now</Link>
              </Button>
            </div>

            {/* Mobile menu button */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  className="lg:hidden flex items-center gap-2 bg-[oklch(0.72_0.18_65)] hover:bg-[oklch(0.65_0.18_65)] text-black font-bold px-3 py-2 rounded-lg"
                >
                  <Menu className="w-5 h-5" />
                  <span className="text-sm">Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-80 bg-[oklch(0.22_0.06_255)] border-white/10 p-0"
              >
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <SheetDescription className="sr-only">Site navigation links</SheetDescription>
                <div className="flex flex-col h-full">
                  {/* Mobile header */}
                  <div className="flex items-center justify-between p-4 border-b border-white/10">
                    <img
                      src={LOGO_URL}
                      alt="Pest Control RAF SERV LTD"
                      className="h-10 w-auto object-contain"
                      width="120"
                      height="40"
                      loading="lazy"
                    />
                  </div>

                  {/* Mobile nav links */}
                  <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    {NAV_LINKS.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                          location === link.href
                            ? "bg-white/20 text-white"
                            : "text-white/80 hover:text-white hover:bg-white/10"
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                    {/* Tools section */}
                    <div className="pt-3 pb-1">
                      <p className="px-4 text-xs font-bold text-white/40 uppercase tracking-widest mb-1">Tools & Offers</p>
                      {TOOLS_LINKS.map((link) => (
                        <Link
                          key={link.href}
                          href={link.href}
                          onClick={() => setMobileOpen(false)}
                          className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                            location === link.href
                              ? "bg-white/20 text-white"
                              : "text-white/80 hover:text-white hover:bg-white/10"
                          }`}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </nav>

                  {/* Mobile CTA */}
                  <div className="p-4 border-t border-white/10 space-y-3">
                    <a
                      href="tel:07586072087" onClick={() => { if (typeof gtag_report_conversion !== "undefined") gtag_report_conversion("tel:07586072087"); }}
                      className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-white/10 text-white font-medium hover:bg-white/20 transition-colors"
                    >
                      <Phone className="w-5 h-5" />
                      07586 072087
                    </a>
                    <Button
                      asChild
                      className="w-full bg-[oklch(0.72_0.18_65)] hover:bg-[oklch(0.65_0.18_65)] text-black font-bold py-3"
                      onClick={() => setMobileOpen(false)}
                    >
                      <Link href="/book">Book Now</Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </>
  );
}
